/**
 * useEmailAuth Hook - Supabase Email/Password Authentication
 *
 * Story: 0-6-frontend-auth-integration
 *
 * This hook provides email/password authentication for merchants using Supabase.
 * Replaces the deprecated backend auth endpoints with direct Supabase client calls.
 *
 * Features:
 * - Sign in with email and password (signInWithPassword)
 * - Loading states per operation
 * - Error handling with user-friendly messages
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { AuthError } from '@supabase/supabase-js';

// ============================================================================
// TYPES
// ============================================================================

interface UseEmailAuthState {
  isLoading: boolean;
  error: string | null;
}

interface UseEmailAuthReturn extends UseEmailAuthState {
  /**
   * Sign in with email and password
   * @param email - User's email address
   * @param password - User's password
   */
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;

  /**
   * Clear any error state
   */
  clearError: () => void;
}

// ============================================================================
// ERROR MESSAGE MAPPING
// ============================================================================

/**
 * Map Supabase auth error codes to user-friendly messages
 */
function getErrorMessage(error: AuthError): string {
  const code = error.message?.toLowerCase() || '';

  // Invalid credentials
  if (code.includes('invalid') && (code.includes('credentials') || code.includes('login'))) {
    return 'Invalid email or password';
  }

  if (code.includes('invalid') && code.includes('email')) {
    return 'Please enter a valid email address';
  }

  // Email not confirmed
  if (code.includes('email') && code.includes('not confirmed')) {
    return 'Please verify your email first';
  }

  // Account disabled/deleted
  if (code.includes('user') && (code.includes('not found') || code.includes('disabled'))) {
    return 'Account not found or disabled';
  }

  // Rate limiting
  if (code.includes('rate') || code.includes('too many')) {
    return 'Too many attempts. Please wait a moment before trying again';
  }

  // Network errors
  if (code.includes('network') || code.includes('fetch')) {
    return 'Network error. Please check your connection';
  }

  // Default fallback
  return error.message || 'An error occurred. Please try again';
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

export function useEmailAuth(): UseEmailAuthReturn {
  const [state, setState] = useState<UseEmailAuthState>({
    isLoading: false,
    error: null,
  });

  /**
   * Sign in with email and password
   */
  const signIn = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    setState({ isLoading: true, error: null });

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const message = getErrorMessage(error);
        setState({ isLoading: false, error: message });
        return { success: false, error: message };
      }

      // Session is automatically stored by Supabase in localStorage
      // The AuthProvider will detect the session change via onAuthStateChange

      setState({ isLoading: false, error: null });
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to sign in';
      setState({ isLoading: false, error: message });
      return { success: false, error: message };
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    isLoading: state.isLoading,
    error: state.error,
    signIn,
    clearError,
  };
}

export default useEmailAuth;
